/**
 * Which deployment this build is for.
 *
 * Not `NODE_ENV`. Vercel builds **every** deployment with `NODE_ENV=production`, previews
 * included, so a `NODE_ENV === 'production'` check hides a page from the preview URLs as well,
 * which is where the developer-only pages are actually needed. Verified on a live preview:
 * `/dev/onboarding-banner-preview`, whose guard used `NODE_ENV`, returned 404 on the preview
 * deployment and not only on production.
 */
export const PRODUCTION_APP_ENV = 'production';

/**
 * True when this build is the production deployment.
 *
 * Two independent signals, and **either** is enough:
 *
 * - `VERCEL_ENV` is set by Vercel itself (`production` / `preview`, unset locally). Nobody has to
 *   remember to configure it, which is why this repo already uses it for the server-side calls
 *   whose failure mode is a leak (`apps/worker-app/middleware.ts`, the noindex headers).
 * - `NEXT_PUBLIC_APP_ENV` is ours, set per Vercel environment (`production` / `staging`, unset
 *   locally), and is the convention in `_app.tsx`, `base-url.ts` and both `next.config.js`.
 *
 * Read together rather than picking one: a guard that hides a page in production must not rest on
 * a single value a human has to keep set. If either says production, treat it as production.
 * Both unset means local, where there is no production to protect and the developer pages are the
 * point, so the pages stay reachable.
 */
export const isProductionDeployment = (): boolean =>
  process.env.VERCEL_ENV === PRODUCTION_APP_ENV || process.env.NEXT_PUBLIC_APP_ENV === PRODUCTION_APP_ENV;

/**
 * `getStaticProps` for a page that must exist everywhere except the production deployment.
 *
 * The props factory is a callback rather than a value so it is never invoked in production:
 * a developer page's props are loaded only when the page is actually going to be served.
 *
 * Pages under `pages/` cannot be imported by jest in these apps without changing the babel
 * transform, so this is where the decision lives and where it is tested: the page keeps a single
 * line and this function holds the behaviour.
 */
export const devOnlyStaticProps = async <T>(loadProps: () => Promise<T>): Promise<{ notFound: true } | { props: T }> =>
  isProductionDeployment() ? { notFound: true } : { props: await loadProps() };
